/**
 * OrbitControls for Phantasy Live2D SDK
 * Provides zoom and pan functionality for Live2D characters
 */

import { OrbitControlsConfig } from '../types/config';
import type { PIXIApplication, Live2DModel } from '../types/live2d';
import { createLogger } from '../utils/logger';

export class OrbitControls {
  private logger = createLogger('OrbitControls');
  private model: Live2DModel;
  private container: HTMLElement;
  private config: Required<OrbitControlsConfig>;
  private isEnabled = true;

  // State tracking
  private isDragging = false;
  private dragButton = -1;
  private lastPointerX = 0;
  private lastPointerY = 0;

  // Transform state
  private currentZoom = 1;
  private panX = 0;
  private panY = 0;
  private originalX = 0;
  private originalY = 0;

  // Touch state for mobile
  private touches: Touch[] = [];
  private lastPinchDistance = 0;

  // Cleanup functions
  private cleanupFunctions: Array<() => void> = [];

  constructor(
    _app: PIXIApplication, // Prefix with underscore to indicate it's intentionally unused
    model: Live2DModel,
    container: HTMLElement,
    config: OrbitControlsConfig = {}
  ) {
    console.log('OrbitControls: CONSTRUCTOR CALLED', {
      config,
      container: container?.tagName,
      containerID: container?.id,
      model: !!model,
    });

    // Note: app parameter kept for compatibility but not stored
    this.model = model;
    this.container = container;
    this.config = this.getDefaultConfig(config);

    console.log('OrbitControls: Final config:', this.config);

    if (this.config.enabled) {
      console.log('OrbitControls: Config enabled, calling initialize');
      this.initialize();
    } else {
      console.log('OrbitControls: Config disabled, NOT initializing');
    }
  }

  private getDefaultConfig(userConfig: OrbitControlsConfig): Required<OrbitControlsConfig> {
    return {
      enabled: true,
      enableZoom: true,
      enablePan: true,
      zoomSpeed: 1.0,
      panSpeed: 1.0,
      minZoom: 0.1,
      maxZoom: 3.0,
      zoomSensitivity: 0.01, // Increased for more responsive zooming
      panBounds: {
        left: -500,
        right: 500,
        top: -500,
        bottom: 500,
      },
      mouseButtons: {
        pan: 0, // Left mouse button
        zoom: true, // Mouse wheel
      },
      touchGestures: {
        pinchZoom: true,
        panTouch: true,
      },
      ...userConfig,
    } as Required<OrbitControlsConfig>;
  }

  private initialize(): void {
    console.log('OrbitControls: INITIALIZING', {
      enabled: this.config.enabled,
      enableZoom: this.config.enableZoom,
      enablePan: this.config.enablePan,
      container: this.container,
      model: !!this.model,
    });

    this.logger.info('Initializing orbit controls');

    // Store original model position
    if (this.model) {
      this.originalX = this.model.x;
      this.originalY = this.model.y;
      this.logger.debug(`Stored original position: (${this.originalX}, ${this.originalY})`);
      console.log('OrbitControls: Original position stored:', this.originalX, this.originalY);
    }

    // Set up mouse events
    this.setupMouseEvents();
    console.log('OrbitControls: Mouse events set up');

    // Set up touch events for mobile
    this.setupTouchEvents();
    console.log('OrbitControls: Touch events set up');

    // Set up wheel events for zoom
    if (this.config.enableZoom && this.config.mouseButtons.zoom) {
      this.setupWheelEvents();
      console.log('OrbitControls: Wheel events set up');
    }

    // Apply initial transform
    this.updateTransform();
    console.log('OrbitControls: INITIALIZATION COMPLETE');
  }

  private setupMouseEvents(): void {
    const mouseDownHandler = (e: MouseEvent) => {
      console.log('OrbitControls: mousedown event', {
        enabled: this.isEnabled,
        enablePan: this.config.enablePan,
        button: e.button,
        expectedButton: this.config.mouseButtons.pan,
      });

      if (!this.isEnabled || !this.config.enablePan) return;

      if (e.button === this.config.mouseButtons.pan) {
        this.isDragging = true;
        this.dragButton = e.button;
        this.lastPointerX = e.clientX;
        this.lastPointerY = e.clientY;
        this.container.style.cursor = 'grabbing';
        e.preventDefault();
        console.log('OrbitControls: drag started');
      }
    };

    const mouseMoveHandler = (e: MouseEvent) => {
      if (!this.isEnabled || !this.isDragging || e.button !== -1) return;

      const deltaX = e.clientX - this.lastPointerX;
      const deltaY = e.clientY - this.lastPointerY;

      this.pan(deltaX, deltaY);

      this.lastPointerX = e.clientX;
      this.lastPointerY = e.clientY;
    };

    const mouseUpHandler = (e: MouseEvent) => {
      if (!this.isEnabled) return;

      if (e.button === this.dragButton) {
        this.isDragging = false;
        this.dragButton = -1;
        this.container.style.cursor = 'default';
      }
    };

    // Add event listeners
    this.container.addEventListener('mousedown', mouseDownHandler);
    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);

    // Store cleanup functions
    this.cleanupFunctions.push(() => {
      this.container.removeEventListener('mousedown', mouseDownHandler);
      document.removeEventListener('mousemove', mouseMoveHandler);
      document.removeEventListener('mouseup', mouseUpHandler);
    });
  }

  private setupWheelEvents(): void {
    const wheelHandler = (e: WheelEvent) => {
      console.log('OrbitControls: wheel event', {
        enabled: this.isEnabled,
        enableZoom: this.config.enableZoom,
        deltaY: e.deltaY,
        sensitivity: this.config.zoomSensitivity,
      });

      if (!this.isEnabled || !this.config.enableZoom) return;

      e.preventDefault();

      // Determine zoom direction and amount
      const delta = e.deltaY * this.config.zoomSensitivity;
      const zoomFactor = 1 - delta;

      console.log('OrbitControls: zooming', {
        delta,
        zoomFactor,
        currentZoom: this.currentZoom,
      });
      this.zoom(zoomFactor, e.clientX, e.clientY);
    };

    this.container.addEventListener('wheel', wheelHandler, { passive: false });

    this.cleanupFunctions.push(() => {
      this.container.removeEventListener('wheel', wheelHandler);
    });
  }

  private setupTouchEvents(): void {
    if (!this.config.touchGestures.pinchZoom && !this.config.touchGestures.panTouch) return;

    const touchStartHandler = (e: TouchEvent) => {
      if (!this.isEnabled) return;

      this.touches = Array.from(e.touches);

      if (this.touches.length === 1 && this.config.touchGestures.panTouch) {
        // Single touch - start panning
        this.isDragging = true;
        this.lastPointerX = this.touches[0].clientX;
        this.lastPointerY = this.touches[0].clientY;
      } else if (this.touches.length === 2 && this.config.touchGestures.pinchZoom) {
        // Two touches - start pinch zoom
        this.isDragging = false;
        this.lastPinchDistance = this.getPinchDistance();
      }

      e.preventDefault();
    };

    const touchMoveHandler = (e: TouchEvent) => {
      if (!this.isEnabled) return;

      this.touches = Array.from(e.touches);

      if (this.touches.length === 1 && this.isDragging && this.config.touchGestures.panTouch) {
        // Single touch panning
        const deltaX = this.touches[0].clientX - this.lastPointerX;
        const deltaY = this.touches[0].clientY - this.lastPointerY;

        this.pan(deltaX, deltaY);

        this.lastPointerX = this.touches[0].clientX;
        this.lastPointerY = this.touches[0].clientY;
      } else if (this.touches.length === 2 && this.config.touchGestures.pinchZoom) {
        // Pinch zoom
        const pinchDistance = this.getPinchDistance();
        const zoomFactor = pinchDistance / this.lastPinchDistance;

        // Get center point of pinch
        const centerX = (this.touches[0].clientX + this.touches[1].clientX) / 2;
        const centerY = (this.touches[0].clientY + this.touches[1].clientY) / 2;

        this.zoom(zoomFactor, centerX, centerY);
        this.lastPinchDistance = pinchDistance;
      }

      e.preventDefault();
    };

    const touchEndHandler = (e: TouchEvent) => {
      if (!this.isEnabled) return;

      this.touches = Array.from(e.touches);

      if (this.touches.length === 0) {
        this.isDragging = false;
      }
    };

    this.container.addEventListener('touchstart', touchStartHandler, {
      passive: false,
    });
    this.container.addEventListener('touchmove', touchMoveHandler, {
      passive: false,
    });
    this.container.addEventListener('touchend', touchEndHandler, {
      passive: false,
    });

    this.cleanupFunctions.push(() => {
      this.container.removeEventListener('touchstart', touchStartHandler);
      this.container.removeEventListener('touchmove', touchMoveHandler);
      this.container.removeEventListener('touchend', touchEndHandler);
    });
  }

  private getPinchDistance(): number {
    if (this.touches.length < 2) return 0;

    const dx = this.touches[0].clientX - this.touches[1].clientX;
    const dy = this.touches[0].clientY - this.touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private pan(deltaX: number, deltaY: number): void {
    if (!this.config.enablePan) return;

    // Apply pan speed
    const panDeltaX = deltaX * this.config.panSpeed;
    const panDeltaY = deltaY * this.config.panSpeed;

    // Update pan position with bounds checking
    this.panX = this.clamp(
      this.panX + panDeltaX,
      this.config.panBounds.left || -Infinity,
      this.config.panBounds.right || Infinity
    );

    this.panY = this.clamp(
      this.panY + panDeltaY,
      this.config.panBounds.top || -Infinity,
      this.config.panBounds.bottom || Infinity
    );

    this.updateTransform();
  }

  private zoom(zoomFactor: number, centerX?: number, centerY?: number): void {
    if (!this.config.enableZoom) return;

    // Apply zoom speed
    const adjustedZoomFactor = 1 + (zoomFactor - 1) * this.config.zoomSpeed;
    const newZoom = this.currentZoom * adjustedZoomFactor;

    // Clamp zoom within bounds
    const clampedZoom = this.clamp(newZoom, this.config.minZoom, this.config.maxZoom);

    if (clampedZoom === this.currentZoom) return;

    // Zoom towards center point (if provided)
    if (centerX !== undefined && centerY !== undefined) {
      const containerRect = this.container.getBoundingClientRect();
      const containerCenterX = containerRect.left + containerRect.width / 2;
      const containerCenterY = containerRect.top + containerRect.height / 2;

      const offsetX = centerX - containerCenterX;
      const offsetY = centerY - containerCenterY;

      // Adjust pan to zoom towards the cursor/touch point
      const zoomRatio = clampedZoom / this.currentZoom;
      this.panX = this.panX - offsetX * (zoomRatio - 1);
      this.panY = this.panY - offsetY * (zoomRatio - 1);
    }

    this.currentZoom = clampedZoom;
    this.updateTransform();
  }

  private updateTransform(): void {
    if (!this.model) return;

    // Apply scale
    this.model.scale.set(this.currentZoom);

    // Set position relative to original position with pan offset
    this.model.x = this.originalX + this.panX;
    this.model.y = this.originalY + this.panY;

    this.logger.debug(
      `Transform updated: zoom=${this.currentZoom.toFixed(2)}, pan=(${this.panX.toFixed(1)}, ${this.panY.toFixed(1)}), pos=(${this.model.x.toFixed(1)}, ${this.model.y.toFixed(1)})`
    );
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }

  // Public API methods
  public enable(): void {
    this.isEnabled = true;
    this.logger.debug('Orbit controls enabled');
  }

  public disable(): void {
    this.isEnabled = false;
    this.isDragging = false;
    this.container.style.cursor = 'default';
    this.logger.debug('Orbit controls disabled');
  }

  public setZoom(zoom: number): void {
    this.currentZoom = this.clamp(zoom, this.config.minZoom, this.config.maxZoom);
    this.updateTransform();
  }

  public setPan(x: number, y: number): void {
    this.panX = this.clamp(
      x,
      this.config.panBounds.left || -Infinity,
      this.config.panBounds.right || Infinity
    );
    this.panY = this.clamp(
      y,
      this.config.panBounds.top || -Infinity,
      this.config.panBounds.bottom || Infinity
    );
    this.updateTransform();
  }

  public reset(): void {
    this.currentZoom = 1;
    this.panX = 0;
    this.panY = 0;
    this.updateTransform();
    this.logger.debug('Orbit controls reset to default');
  }

  public getState(): { zoom: number; panX: number; panY: number } {
    return {
      zoom: this.currentZoom,
      panX: this.panX,
      panY: this.panY,
    };
  }

  public updateConfig(newConfig: Partial<OrbitControlsConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.logger.debug('Orbit controls config updated');
  }

  public destroy(): void {
    this.disable();

    // Clean up all event listeners
    this.cleanupFunctions.forEach(cleanup => cleanup());
    this.cleanupFunctions = [];

    this.logger.debug('Orbit controls destroyed');
  }
}
